import * as PIXI from "pixi.js";

import { Spine, TrackEntry, Animation } from "@pixi/spine-pixi";
import { SymbolReel } from "@src/game/symbol-panel/SymbolReel";
import { EventSystem } from "@src/core/events/EventSystem";
import { IView } from "@src/core/game/components/IView";
import { Symbol } from "@src/game/symbol-panel/Symbol";
import { gameEngine } from "@src/game/GameEngine";
import { constants } from "@src/game/Constants";
import gsap from "gsap";

// TODO: Maybe create a pool for these later.

// Each reel can logically have up to 2 wild visualizers.
export class ExpandedWildVisualizer implements IView {
  private _view: PIXI.Container = new PIXI.Container();
  public get view(): PIXI.Container {
    return this._view;
  }

  // Use getters, since some static properties may not be initialized yet.
  public static get INITIAL_Y(): number {
    return -Symbol.HEIGHT * gameEngine.config.rows + Symbol.HEIGHT / 2;
  }

  public static get ONE_SYMBOL_SCROLL_TARGET_Y(): number {
    return ExpandedWildVisualizer.INITIAL_Y + Symbol.HEIGHT;
  }

  public static get FULL_SCROLL_TARGET_Y(): number {
    return (
      ExpandedWildVisualizer.INITIAL_Y + Symbol.HEIGHT * gameEngine.config.rows
    );
  }

  public static get OFF_SCREEN_ONE_SYMBOL_SCROLL_TARGET_Y(): number {
    return ExpandedWildVisualizer.FULL_SCROLL_TARGET_Y + Symbol.HEIGHT;
  }

  public static get OFF_SCREEN_FULL_SCROLL_TARGET_Y(): number {
    return (
      ExpandedWildVisualizer.FULL_SCROLL_TARGET_Y +
      Symbol.HEIGHT * gameEngine.config.rows
    );
  }

  // public static readonly FULL_SCROLL_TARGET_Y: number =
  //   Symbol.HEIGHT * gameEngine.config.rows + SymbolReel.WORLD_Y_OFFSET;

  private _expandedWildVisualizerSkeleton!: Spine;

  private _reelPlayAnimationTimeline!: GSAPTimeline;
  private _reelStopAnimationTimeline!: GSAPTimeline;

  public isActive(): boolean {
    return (
      (this._reelPlayAnimationTimeline !== undefined &&
        this._reelPlayAnimationTimeline.isActive()) ||
      (this._reelStopAnimationTimeline !== undefined &&
        this._reelStopAnimationTimeline.isActive())
    );
  }

  public constructor() {
    this._view.y = ExpandedWildVisualizer.INITIAL_Y;

    this._initializeAssets();
    this._parentAssets();
  }

  public playWinAnimation(): void {
    console.log("playing win animation on expanded wild visualizer");
    this._expandedWildVisualizerSkeleton.state.setAnimation(
      0,
      "match_expand",
      false,
    );
    this._expandedWildVisualizerSkeleton.state.addAnimation(
      0,
      "idle_expand",
      true,
    );

    this._view.tint = 0xffffff;

    // requestAnimationFrame(() => {
    //   EventSystem.invoke(
    //     constants.GameEvents.SYMBOL_WIN_ANIMATION_STARTED,
    //     this._view,
    //   );
    // });
  }

  public playSpinAnimation(): void {
    this._reelStopAnimationTimeline?.kill();
    this._reelPlayAnimationTimeline?.kill();
    this._reelPlayAnimationTimeline = gsap.timeline();

    this._reelPlayAnimationTimeline
      .to(this._view, {
        y: ExpandedWildVisualizer.OFF_SCREEN_ONE_SYMBOL_SCROLL_TARGET_Y,
        duration: SymbolReel.SPIN_DURATION_INITIAL_S,
        ease: "back.in(3)",
      })
      .to(this._view, {
        // The visualizer only needs to go off-screen.
        y: ExpandedWildVisualizer.OFF_SCREEN_FULL_SCROLL_TARGET_Y,
        duration: SymbolReel.SPIN_ITERATION_DURATION_S * 3,
        ease: "none",
        onComplete: () => {
          this._view.removeFromParent();
        },
      });
  }

  public playStopAnimation(duration: number): void {
    this._view.y = ExpandedWildVisualizer.INITIAL_Y;
    this._reelPlayAnimationTimeline?.kill();
    this._reelStopAnimationTimeline?.kill();
    this._reelStopAnimationTimeline = gsap.timeline();

    this._reelStopAnimationTimeline
      .to(this._view, {
        y: ExpandedWildVisualizer.ONE_SYMBOL_SCROLL_TARGET_Y,
        duration: SymbolReel.WILD_INITIAL_STOP_DURATION_S,
        ease: "back.out(0.4)",
      })
      .to(this._view, {
        y: ExpandedWildVisualizer.FULL_SCROLL_TARGET_Y,
        duration: duration,
        delay: SymbolReel.WILD_DELAY_DURATION_S,
        ease: "quad.out(0.4)",
      });
  }

  public playSwiftSpinAnimation(): void {
    this._reelPlayAnimationTimeline?.kill();
    this._reelStopAnimationTimeline?.kill();
    this._reelStopAnimationTimeline = gsap.timeline();

    this._reelStopAnimationTimeline.to(this._view, {
      y: ExpandedWildVisualizer.FULL_SCROLL_TARGET_Y,
      duration: SymbolReel.SWIFT_STOP_DURATION_S,
      ease: "quad.out(0.4)",
    });
  }

  public playSwiftStopAnimation(): void {
    this._reelPlayAnimationTimeline?.kill();
    this._reelStopAnimationTimeline?.kill();
    this._reelStopAnimationTimeline = gsap.timeline();

    this._reelStopAnimationTimeline.to(this._view, {
      y: ExpandedWildVisualizer.OFF_SCREEN_FULL_SCROLL_TARGET_Y,
      duration: SymbolReel.SWIFT_STOP_DURATION_S,
      ease: "quad.out(0.4)",
      onComplete: () => {
        this._view.removeFromParent();
      },
    });
  }

  private _initializeAssets(): void {
    this._expandedWildVisualizerSkeleton = Spine.from({
      skeleton: "wildAtlasJson",
      atlas: "wildAtlas",
    });
    this._expandedWildVisualizerSkeleton.skeleton.setSkinByName("default");
    this._expandedWildVisualizerSkeleton.skeleton.setSlotsToSetupPose();
    this._expandedWildVisualizerSkeleton.scale.set(0.12);
    this._expandedWildVisualizerSkeleton.state.setAnimation(0, "expand", false);
    this._expandedWildVisualizerSkeleton.state.getCurrent(0)!.trackTime =
      this._expandedWildVisualizerSkeleton.state.getCurrent(0)!.animationEnd;
    this._expandedWildVisualizerSkeleton.state.addAnimation(
      0,
      "idle_expand",
      true,
    );

    this._expandedWildVisualizerSkeleton.state.addListener({
      complete: () => {
        const currentTrack: TrackEntry | null =
          this._expandedWildVisualizerSkeleton.state.getCurrent(0);

        if (currentTrack) {
          const animation: Animation | null = currentTrack.animation;

          if (animation && animation.name.includes("match")) {
            // console.log("hamburger");
            EventSystem.invoke(
              constants.GameEvents.SYMBOL_WIN_ANIMATION_ENDED,
              this._view,
            );
          }
        }
      },
    });
  }

  private _parentAssets(): void {
    this._view.addChild(this._expandedWildVisualizerSkeleton);
  }
}
